import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import Logo from '../components/Logo';
import GoogleSignIn from '../components/GoogleSignIn';
import { roleHome, ORGANIZER_ONBOARDING } from '../config/brand';

const Register = ({ individualOnly = false }) => {
  // The dedicated Meko registration never exposes or accepts a professional role.
  // Preserve legacy /register?role=individual links without a role picker.
  const individualMode = individualOnly || new URLSearchParams(window.location.search).get('role') === 'individual';
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState(() => individualMode ? 'individual' : '');
  const accountRole = individualMode ? 'individual' : role;
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError(''); setErrorCode('');
    if (!accountRole) { setError('Önce hesap türünü seç.'); return; }
    if (password.length < 8) { setError('Şifre en az 8 karakter olmalı.'); return; }
    setLoading(true);
    try {
      const result = await register(email.trim(), password, name.trim(), accountRole);
      if (result.success) {
        const r = result.user?.role || accountRole;
        navigate(r === 'organizer' ? ORGANIZER_ONBOARDING : r === 'venue' ? '/venue/profile' : r === 'individual' ? '/individual' : roleHome(r), { replace: true });
      } else { setError(result.error); setErrorCode(result.code || ''); }
    } catch { setError('Bağlantı kurulamadı. Bilgilerin burada; tekrar deneyebilirsin.'); }
    finally { setLoading(false); }
  };
  const nextField = id => e => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault(); e.currentTarget.form.elements.namedItem(id)?.focus();
    }
  };

  return (
    <div className={`registration-page min-h-screen bg-surface ${individualMode ? "registration-page-individual" : ""}`} data-testid={individualMode ? "individual-registration" : "professional-registration"}>
      <div className={`registration-panel ${individualMode ? "registration-panel-individual" : ""}`}>
        <div className={`registration-heading ${individualMode ? 'registration-heading-individual' : ''}`}>
          <Logo size="md" />
          {individualMode && <div className="registration-meko-intro" aria-hidden="true">
            <img src="/brand/meko-cat-original-28ba77df.jpg" alt="" width="76" height="76" />
          </div>}
          {individualMode && <span className="registration-meko-eyebrow">BİREYSEL · MEKO SENİNLE</span>}
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900" data-testid="register-title">
            {individualMode ? "Meko'yla planlamaya başla" : 'Kayıt Ol'}
          </h1>
          <p className="mt-2 text-sm text-zinc-500">
            {individualMode
              ? "Sadece hesabını oluştur. Sonra doğum günü, buluşma veya toplantı planını Meko'ya yazarak ya da sesle anlat."
              : 'Ücretsiz hesabın hemen oluşur. Profilini tamamladıktan sonra eşleşme ve talepler için ekip onayı gerekir.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="register-form" className="space-y-4">
          {!individualMode && (
          <fieldset disabled={loading}>
            <legend className="block text-sm text-zinc-600 mb-1.5">Hesap türü</legend>
            <div className="registration-roles" role="radiogroup" aria-label="Hesap türü" data-testid="register-role-select">
              {[
                { value: 'venue', label: 'Mekân', hint: 'Mekânımı eklemek istiyorum.' },
                { value: 'organizer', label: 'Organizatör',
                  hint: 'Etkinlik düzenliyorum.' },
              ].map(opt => (
                <label key={opt.value}
                  className={`registration-role rounded-lg border cursor-pointer transition-colors ${
                    role === opt.value ? 'border-[#1E3A8A] bg-[#E6EEFF]/60' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}
                  data-testid={`register-role-${opt.value}`}>
                  <input type="radio" name="role" value={opt.value} checked={role === opt.value}
                    onChange={() => setRole(opt.value)} className="accent-[#1E3A8A]" required />
                  <span>
                    <span className="block text-sm font-medium text-zinc-900">{opt.label}</span>
                    <span className="block text-xs text-zinc-500 mt-0.5">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          )}
          <div>
            <label className="block text-sm text-zinc-600 mb-1.5" htmlFor="name">{individualMode ? "Adın ve soyadın" : "İsim"}</label>
            <input id="name" enterKeyHint="next" onKeyDown={nextField("email")} autoComplete="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 border border-zinc-200 rounded-lg bg-white text-sm placeholder-zinc-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 transition-all"
              placeholder={individualMode ? "Adın Soyadın" : "Ad Soyad"} required data-testid="register-name-input" />
          </div>

          <div>
            <label className="block text-sm text-zinc-600 mb-1.5" htmlFor="email">E-posta</label>
            <input id="email" inputMode="email" autoCapitalize="none" spellCheck={false} enterKeyHint="next" onKeyDown={nextField("password")} autoComplete="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border border-zinc-200 rounded-lg bg-white text-sm placeholder-zinc-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 transition-all"
              placeholder="ornek@firma.com" required data-testid="register-email-input" />
          </div>

          <div>
            <label className="block text-sm text-zinc-600 mb-1.5" htmlFor="password">Şifre</label>
            <div className="registration-password">
            <input id="password" enterKeyHint="done" autoComplete="new-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-zinc-200 rounded-lg bg-white text-sm placeholder-zinc-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 transition-all"
              placeholder="En az 8 karakter" required minLength={8} maxLength={128} data-testid="register-password-input" />
            <button type="button" className="registration-password-toggle" aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"} aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)}>{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button>
            </div>
          </div>



          {error && (
            <div className="px-4 py-3 text-sm bg-[#1E3A8A]/10 text-[#9A4F00] border border-[#1E3A8A]/30 rounded-lg" role="alert" data-testid="register-error">
              <p>{error}</p>
              {['EMAIL_ALREADY_REGISTERED', 'REGISTRATION_UNCONFIRMED'].includes(errorCode) && <div className="flex flex-wrap gap-4 mt-3">
                <Link to={individualMode ? "/login?role=individual" : "/login"} state={{ email: email.trim() }} className="font-semibold underline">Giriş yap</Link>
                <Link to="/forgot-password" state={{ email: email.trim() }} className="font-semibold underline">Şifre oluştur / yenile</Link>
              </div>}
            </div>
          )}

          <button type="submit" disabled={loading}
            className="registration-submit w-full text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
            data-testid="register-submit-button">
            {loading ? 'Hesabın oluşturuluyor…' : individualMode ? "Ücretsiz hesabını aç ve Meko'ya geç" : 'Kayıt Ol'}
          </button>
        </form>
        {!individualMode && <GoogleSignIn role={role} disabled={loading || !role} />}
        {individualMode && <p className="registration-meko-assurance">Günde 2 ücretsiz rezervasyon talebi hakkın var. Meko sen onaylamadan mekânlara talep göndermez.</p>}
        {!individualMode && <p className="registration-personal-link">Kendi buluşman için mekân mı arıyorsun? <Link to="/bireysel/kayit">Meko'yla planla</Link></p>}

        <p className="mt-6 text-center text-sm text-zinc-500">
          {individualMode ? "Zaten bireysel hesabın var mı?" : "Zaten hesabınız var mı?"}{' '}
          <Link to={individualMode ? "/login?role=individual" : "/login"} className="text-[#1E3A8A] hover:underline font-medium" data-testid="register-login-link">
            Giriş Yap
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;


